export const KERNEL_CANVAS_ARTIFACT_SCHEMA = "origin.kernel-canvas";
export const KERNEL_CANVAS_ARTIFACT_FORMAT_VERSION = 1;
export const DEFAULT_KERNEL_CANVAS_ARTIFACT_LIMITS = Object.freeze({
    maxStepsPerKernel: 20,
    maxRowsPerCanvas: 20,
    maxCellsPerCanvas: 20,
    maxTitleBytes: 255,
    maxDefinitionBytes: 256 * 1024,
    maxJsonDepth: 32,
});

const UUID_V4 = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const NAME = /^[a-z0-9][a-z0-9_-]{0,63}$/;
const KNOWN_KINDS = new Set(["kernel", "canvas"]);
const textEncoder = new TextEncoder();

function isObject(value) {
    return value !== null && typeof value === "object" && !Array.isArray(value);
}

function jsonClone(value) {
    return JSON.parse(JSON.stringify(value));
}

function portableObject(kind, definition) {
    const object = jsonClone(definition);
    delete object.builtin;
    delete object.createdAt;
    delete object.updatedAt;
    object.kind = kind;
    return object;
}

function edgeKey(canvasId, kernelId) {
    return `${canvasId}:${kernelId}`;
}

function cellEdges(objects) {
    const edges = new Map();
    for (const object of objects) {
        if (object?.kind !== "canvas" || !Array.isArray(object.rows)) continue;
        for (const row of object.rows) {
            if (!Array.isArray(row?.cells)) continue;
            for (const cell of row.cells) {
                if (typeof cell?.kernel_id !== "string") continue;
                const edge = { canvas_id: object.id, kernel_id: cell.kernel_id };
                edges.set(edgeKey(edge.canvas_id, edge.kernel_id), edge);
            }
        }
    }
    return [...edges.values()].sort((a, b) =>
        a.canvas_id.localeCompare(b.canvas_id) || a.kernel_id.localeCompare(b.kernel_id));
}

function artifactFromObjects(objects) {
    return {
        schema: KERNEL_CANVAS_ARTIFACT_SCHEMA,
        format_version: KERNEL_CANVAS_ARTIFACT_FORMAT_VERSION,
        objects,
        dependencies: cellEdges(objects),
    };
}

export function createKernelCanvasArtifact({ kernels = [], canvases = [] } = {}) {
    return artifactFromObjects([
        ...kernels.map((kernel) => portableObject("kernel", kernel)),
        ...canvases.map((canvas) => portableObject("canvas", canvas)),
    ]);
}

function addError(errors, code, path, message) {
    errors.push({ code, path, message });
}

function finiteLimit(limits, name) {
    const value = limits?.[name];
    return Number.isFinite(value) && value >= 0 ? value : null;
}

function jsonBytes(value) {
    return textEncoder.encode(JSON.stringify(value)).byteLength;
}

function restorableDefinition(object) {
    const definition = { ...object };
    delete definition.id;
    delete definition.kind;
    return definition;
}

function jsonDepth(value, stopAfter) {
    if (value === null || typeof value !== "object") return 0;
    const active = new WeakSet();
    const stack = [{ value, depth: 1, leaving: false }];
    let maximum = 0;
    while (stack.length) {
        const current = stack.pop();
        if (current.leaving) {
            active.delete(current.value);
            continue;
        }
        if (active.has(current.value)) throw new TypeError("cyclic JSON value");
        maximum = Math.max(maximum, current.depth);
        if (maximum > stopAfter) return maximum;
        active.add(current.value);
        stack.push({ value: current.value, depth: current.depth, leaving: true });
        const children = Array.isArray(current.value)
            ? current.value
            : Object.values(current.value);
        for (let index = children.length - 1; index >= 0; index -= 1) {
            const child = children[index];
            if (child !== null && typeof child === "object") {
                stack.push({ value: child, depth: current.depth + 1, leaving: false });
            }
        }
    }
    return maximum;
}

function validateLimit(errors, limits, name, actual, path) {
    const limit = finiteLimit(limits, name);
    if (limit !== null && actual > limit) {
        addError(errors, "bound_exceeded", path, `${name} is ${actual}; maximum is ${limit}`);
    }
}

export function validateKernelCanvasArtifact(artifact, { limits = {} } = {}) {
    const effectiveLimits = {
        ...DEFAULT_KERNEL_CANVAS_ARTIFACT_LIMITS,
        ...(limits || {}),
    };
    const errors = [];
    if (!isObject(artifact)) {
        addError(errors, "invalid_artifact", "$", "artifact must be an object");
        return { ok: false, errors };
    }
    if (artifact.schema !== KERNEL_CANVAS_ARTIFACT_SCHEMA) {
        addError(errors, "invalid_schema", "$.schema", `expected ${KERNEL_CANVAS_ARTIFACT_SCHEMA}`);
    }
    if (artifact.format_version !== KERNEL_CANVAS_ARTIFACT_FORMAT_VERSION) {
        addError(errors, "unsupported_version", "$.format_version", `expected ${KERNEL_CANVAS_ARTIFACT_FORMAT_VERSION}`);
    }
    if (!Array.isArray(artifact.objects)) {
        addError(errors, "invalid_objects", "$.objects", "objects must be an array");
        return { ok: false, errors };
    }
    if (!Array.isArray(artifact.dependencies)) {
        addError(errors, "invalid_dependencies", "$.dependencies", "dependencies must be an array");
    }

    const objectsById = new Map();
    const commands = new Map();
    const slugs = new Map();
    let kernelCount = 0;
    let canvasCount = 0;

    validateLimit(errors, effectiveLimits, "maxObjects", artifact.objects.length, "$.objects");

    artifact.objects.forEach((object, index) => {
        const path = `$.objects[${index}]`;
        if (!isObject(object)) {
            addError(errors, "invalid_object", path, "artifact object must be an object");
            return;
        }
        if (!KNOWN_KINDS.has(object.kind)) {
            addError(errors, "invalid_kind", `${path}.kind`, "kind must be kernel or canvas");
        }
        if (!UUID_V4.test(object.id || "")) {
            addError(errors, "invalid_uuid", `${path}.id`, "id must be a UUIDv4");
        } else if (objectsById.has(object.id)) {
            addError(errors, "duplicate_uuid", `${path}.id`, `duplicate id ${object.id}`);
        } else {
            objectsById.set(object.id, object);
        }
        if (typeof object.title !== "string" || !object.title.trim()) {
            addError(errors, "invalid_title", `${path}.title`, "title must not be blank");
        } else {
            validateLimit(
                errors,
                effectiveLimits,
                "maxTitleBytes",
                textEncoder.encode(object.title).byteLength,
                `${path}.title`,
            );
        }

        try {
            const definition = restorableDefinition(object);
            validateLimit(
                errors,
                effectiveLimits,
                "maxDefinitionBytes",
                jsonBytes(definition),
                path,
            );
            const depthLimit = finiteLimit(effectiveLimits, "maxJsonDepth");
            if (depthLimit !== null) {
                validateLimit(
                    errors,
                    effectiveLimits,
                    "maxJsonDepth",
                    jsonDepth(definition, depthLimit),
                    path,
                );
            }
        } catch {
            addError(errors, "not_json", path, "object must be JSON-serializable");
        }

        if (object.kind === "kernel") {
            kernelCount += 1;
            if (!NAME.test(object.command || "")) {
                addError(errors, "invalid_command", `${path}.command`, "command must be a canonical command name");
            } else if (commands.has(object.command)) {
                addError(errors, "duplicate_command", `${path}.command`, `duplicate command ${object.command}`);
            } else {
                commands.set(object.command, object.id);
            }
            if (!Array.isArray(object.steps)) {
                addError(errors, "invalid_kernel", `${path}.steps`, "kernel steps must be an array");
            } else {
                validateLimit(errors, effectiveLimits, "maxStepsPerKernel", object.steps.length, `${path}.steps`);
            }
            if (!isObject(object.render)) {
                addError(errors, "invalid_kernel", `${path}.render`, "kernel render must be an object");
            }
        } else if (object.kind === "canvas") {
            canvasCount += 1;
            if (!NAME.test(object.slug || "")) {
                addError(errors, "invalid_slug", `${path}.slug`, "slug must be a canonical slug");
            } else if (slugs.has(object.slug)) {
                addError(errors, "duplicate_slug", `${path}.slug`, `duplicate slug ${object.slug}`);
            } else {
                slugs.set(object.slug, object.id);
            }
            if (!Array.isArray(object.rows)) {
                addError(errors, "invalid_canvas", `${path}.rows`, "canvas rows must be an array");
            } else {
                validateLimit(errors, effectiveLimits, "maxRowsPerCanvas", object.rows.length, `${path}.rows`);
                let cells = 0;
                object.rows.forEach((row, rowIndex) => {
                    if (!Array.isArray(row?.cells)) {
                        addError(errors, "invalid_canvas", `${path}.rows[${rowIndex}].cells`, "row cells must be an array");
                        return;
                    }
                    cells += row.cells.length;
                    row.cells.forEach((cell, cellIndex) => {
                        const cellPath = `${path}.rows[${rowIndex}].cells[${cellIndex}]`;
                        if (!isObject(cell)) {
                            addError(errors, "invalid_cell", cellPath, "cell must be an object");
                        } else if (!UUID_V4.test(cell.kernel_id || "")) {
                            addError(errors, "invalid_dependency", `${cellPath}.kernel_id`, "kernel_id must be a UUIDv4");
                        }
                    });
                });
                validateLimit(errors, effectiveLimits, "maxCellsPerCanvas", cells, `${path}.rows`);
            }
        }
    });

    validateLimit(errors, effectiveLimits, "maxKernels", kernelCount, "$.objects");
    validateLimit(errors, effectiveLimits, "maxCanvases", canvasCount, "$.objects");
    try {
        validateLimit(errors, effectiveLimits, "maxArtifactBytes", jsonBytes(artifact), "$");
    } catch {
        addError(errors, "not_json", "$", "artifact must be JSON-serializable");
    }

    const expectedEdges = new Map(cellEdges(artifact.objects).map((edge) => [
        edgeKey(edge.canvas_id, edge.kernel_id),
        edge,
    ]));
    const declaredEdges = new Map();
    if (Array.isArray(artifact.dependencies)) {
        artifact.dependencies.forEach((edge, index) => {
            const path = `$.dependencies[${index}]`;
            if (!isObject(edge) || !UUID_V4.test(edge.canvas_id || "") || !UUID_V4.test(edge.kernel_id || "")) {
                addError(errors, "invalid_dependency", path, "dependency must contain canvas and kernel UUIDv4 ids");
                return;
            }
            const key = edgeKey(edge.canvas_id, edge.kernel_id);
            if (declaredEdges.has(key)) {
                addError(errors, "duplicate_dependency", path, "dependency edge is duplicated");
            }
            declaredEdges.set(key, edge);
            const canvas = objectsById.get(edge.canvas_id);
            const kernel = objectsById.get(edge.kernel_id);
            if (!canvas) {
                addError(errors, "missing_dependency_source", `${path}.canvas_id`, `missing canvas ${edge.canvas_id}`);
            } else if (canvas.kind !== "canvas") {
                addError(errors, "dependency_kind", `${path}.canvas_id`, `${edge.canvas_id} is not a canvas`);
            }
            if (!kernel) {
                addError(errors, "missing_dependency", `${path}.kernel_id`, `missing kernel ${edge.kernel_id}`);
            } else if (kernel.kind !== "kernel") {
                addError(errors, "dependency_kind", `${path}.kernel_id`, `${edge.kernel_id} is not a kernel`);
            }
            if (!expectedEdges.has(key)) {
                addError(errors, "extra_dependency", path, "dependency is not present in the canvas body");
            }
        });
    }

    for (const [key, edge] of expectedEdges) {
        if (!declaredEdges.has(key)) {
            addError(errors, "missing_dependency_edge", "$.dependencies", `missing edge ${edge.canvas_id} -> ${edge.kernel_id}`);
        }
        const target = objectsById.get(edge.kernel_id);
        if (!target) {
            addError(errors, "missing_dependency", "$.objects", `missing kernel ${edge.kernel_id}`);
        } else if (target.kind !== "kernel") {
            addError(errors, "dependency_kind", "$.objects", `${edge.kernel_id} is not a kernel`);
        }
    }

    return { ok: errors.length === 0, errors };
}

export function assertValidKernelCanvasArtifact(artifact, options) {
    const result = validateKernelCanvasArtifact(artifact, options);
    if (!result.ok) {
        const detail = result.errors.map((error) => `${error.path}: ${error.message}`).join("; ");
        throw new Error(`invalid kernel/canvas artifact: ${detail}`);
    }
    return artifact;
}

export function exportKernelCanvasArtifact(catalog, { canvasIds = [], kernelIds = [] } = {}, options) {
    assertValidKernelCanvasArtifact(catalog, options);
    const byId = new Map(catalog.objects.map((object) => [object.id, object]));
    const selected = new Set();

    for (const id of kernelIds) {
        const object = byId.get(id);
        if (!object || object.kind !== "kernel") throw new Error(`kernel ${id} is not in the catalog`);
        selected.add(id);
    }
    for (const id of canvasIds) {
        const canvas = byId.get(id);
        if (!canvas || canvas.kind !== "canvas") throw new Error(`canvas ${id} is not in the catalog`);
        selected.add(id);
        for (const edge of catalog.dependencies) {
            if (edge.canvas_id === id) selected.add(edge.kernel_id);
        }
    }

    const artifact = artifactFromObjects(
        catalog.objects.filter((object) => selected.has(object.id)).map(jsonClone),
    );
    return assertValidKernelCanvasArtifact(artifact, options);
}

export function isUuidV4(value) {
    return UUID_V4.test(value || "");
}
